/**
 * How /overview prints a number. null is "—" (not measurable), never 0; money
 * always names its currency (formatCurrency(n, true, currency)). Pure.
 *
 * @module services/overview/overviewFormat
 */
import type { TFunction } from 'i18next';
import { formatCurrency } from '@/config/brand';
import { formatDelta } from '@/services/looker/lookerDelta';
import { RESULT_METRIC_NAME } from '@/services/looker/lookerTargetCells';
import type { ResultMetric } from '@/services/targets/resultMetric';
import type { ScoreMetric } from './scorecards';
import type { ResultState } from './overviewTypes';

type TFn = (key: string, options?: Record<string, unknown>) => string;

export const DASH = '—';

export const fmtMoney = (n: number | null, currency: string | null, digits = 2): string =>
  (n === null || currency === null ? DASH : formatCurrency(n, true, currency, digits));

export const fmtCount = (n: number | null): string =>
  (n === null ? DASH : n.toLocaleString('en-US', { maximumFractionDigits: 2 }));

export const fmtPct = (n: number | null, digits = 2): string => (n === null ? DASH : `${(n * 100).toFixed(digits)}%`);

/** ▲ / ▼ always beside a direction, so the move reads without its colour. */
export const fmtDelta = (d: number | null): string => {
  if (d === null) return DASH;
  if (d === 0) return formatDelta(0);
  return `${d > 0 ? '▲' : '▼'} ${formatDelta(d)}`;
};

/**
 * A Result cell (I2): "ไม่ได้รายงาน" only when the platform spent and did not report
 * the metric; "—" when there is no Result definition or nothing to count in the window.
 */
export const fmtResult = (r: { metric: unknown; result: number | null; resultState: ResultState }, t: TFn): string => {
  if (r.metric === null || r.resultState === 'none') return DASH;
  return r.resultState === 'notReported' ? t('overview.notReported') : fmtCount(r.result);
};

/**
 * What a Result counts, as D1's file names it (rules-04 D5): Reach is "Reach รวมรายวัน", as D3's file names it;
 * the others are /looker's RESULT_METRIC_NAME. Boxes 9 and 13 print it under each Result (rules-04 E3).
 */
export const resultMetricName = (metric: ResultMetric, t: TFn): string =>
  (metric === 'reach' ? t('overview.csv.resultMetricReach') : RESULT_METRIC_NAME[metric]);

/** What a Cost per Result is per: "ต้นทุน / 1,000 impressions", "ต้นทุน / click"… Literal keys. */
export const costBasisText = (metric: ResultMetric, t: TFn): string => {
  if (metric === 'impressions') return t('overview.costBasis.impressions');
  if (metric === 'reach') return t('overview.costBasis.reach');
  if (metric === 'clicks') return t('overview.costBasis.clicks');
  return t('overview.costBasis.conversions');
};

/** Box 3's Result label names the metric always, not only under Reach (rules-04 E3); none to name: "Result". */
export const resultLabel = (metric: ResultMetric | null, t: TFn): string => {
  if (metric === 'impressions') return t('overview.resultImpressions');
  if (metric === 'reach') return t('overview.resultReach');
  if (metric === 'clicks') return t('overview.resultClicks');
  if (metric === 'conversions') return t('overview.resultConversions');
  return t('overview.result');
};

/** Box 3's Cost per Result label: "Cost per Result (ต้นทุน / 1,000 impressions)"; none to name: "Cost per Result". */
export const costPerResultLabel = (metric: ResultMetric | null, t: TFn): string =>
  (metric === null ? t('overview.costPerResult') : t('overview.costPerResultOf', { basis: costBasisText(metric, t) }));

/** The score metrics printed as money. One set, shared with the CSV export. */
export const MONEY_METRICS: ReadonlySet<ScoreMetric> = new Set<ScoreMetric>(['spend', 'cpc', 'cpm']);

export const fmtScore = (metric: ScoreMetric, n: number | null, currency: string | null): string =>
  (metric === 'ctr' ? fmtPct(n) : MONEY_METRICS.has(metric) ? fmtMoney(n, currency) : fmtCount(n));

/** Literal keys, so the locale integrity test sees them. */
export const scoreLabel = (metric: ScoreMetric, t: TFunction): string => {
  if (metric === 'spend') return t('overview.metric.spend');
  if (metric === 'impressions') return t('overview.metric.impressions');
  if (metric === 'clicks') return t('overview.metric.clicks');
  if (metric === 'ctr') return t('overview.metric.ctr');
  if (metric === 'cpc') return t('overview.metric.cpc');
  return t('overview.metric.cpm');
};

/** Literal keys, so the locale integrity test sees them. Unknown objectives read as themselves. */
export const objectiveText = (o: string, t: TFn): string => {
  if (o === 'awareness') return t('looker.objectives.awareness');
  if (o === 'consideration') return t('looker.objectives.consideration');
  if (o === 'conversion') return t('looker.objectives.conversion');
  if (o === 'unspecified') return t('insights.objectiveUnspecified');
  return o;
};
